// The talk lab's game (`?scene=talk`; capability round 1, IMP-0044): the conversation and scene-player units from
// lab.json alone, on their own kernel game. Node-safe (no DOM, no three.js): the lab page draws it (lab.ts) and
// tools/talk.test.ts runs it. The one module here is the lab's input: two buttons that say what the player did, as
// the standard `interact:done` (talk to Mira; reach the gate). Everything else is the units' and the content's.
import { createGame, type CompositionManifest, type ContentPack, type GameModule, type OpenEvents } from "@rpg/kernel";
import { createConversation, scriptedOpenTalk, type ConversationContent, type ScriptedPerson, type TalkMode } from "@rpg-unit/conversation";
import { createScenePlayer, type SceneContent } from "@rpg-unit/scene-player";
import LAB from "./lab.json";

/** The lab's actions: what its buttons dispatch. */
export const TALK_LAB_ACTIONS = { talk: "lab/talk", gate: "lab/gate" } as const;

/** `talk`: how Mira talks, at her stall and in the gate scene: "static" (her script; the default) or "open" (typed or
 *  suggested lines; the replies from lab.json's scripted stand-in for an AI provider, the scene's about its topic). */
export function createTalkLab(o: { minutesPerLine?: number; charsPerSecond?: number; talk?: TalkMode } = {}) {
  const talk = createConversation({
    content: LAB.conversation as unknown as ConversationContent,
    minutesPerLine: o.minutesPerLine ?? 2,
    ...(o.charsPerSecond !== undefined ? { charsPerSecond: o.charsPerSecond } : {}),
    talk: o.talk ?? "static",
    openTalk: scriptedOpenTalk(LAB.openTalk as unknown as Record<string, ScriptedPerson>),
  });
  // the gate scene's talk goes open too when the lab talks openly (its script the fallback)
  const scenes = createScenePlayer({ content: LAB.scenes as unknown as SceneContent, talk: o.talk ?? "static" });
  const input: GameModule = {
    id: "lab-input",
    version: "1",
    setup(ctx) {
      ctx.registerAction(TALK_LAB_ACTIONS.talk, () => ctx.events.emit("interact:done", { actor: 1, target: "mira", kind: "npc" }));
      ctx.registerAction(TALK_LAB_ACTIONS.gate, () => ctx.events.emit("interact:done", { actor: 1, target: "gate", kind: "place" }));
    },
  };
  const manifest: CompositionManifest = {
    meta: { id: "talk-lab", displayName: "Talk lab", version: "0.1.0" },
    modules: [{ id: "conversation", version: "0.1.0" }, { id: "scene-player", version: "0.1.0" }, { id: "lab-input", version: "1" }],
    contentPacks: [],
    camera: "three-quarter",
    input: { scheme: "wasd-topdown" },
  };
  const pack: ContentPack = { meta: { id: "talk-lab", displayName: "Talk lab", version: "0.1.0" }, requiresModules: [], build: () => {} };
  const game = createGame({ manifest, moduleRegistry: { conversation: talk.module<OpenEvents>(), "scene-player": scenes.module<OpenEvents>(), "lab-input": input }, content: pack });
  return { game, talk, scenes };
}
